import { useEffect, useMemo, useRef } from 'react';

import { useFlagsStore } from '@/lib/client-store';

/**
 * Hook to check if debug mode is enabled and toggle it
 * Only allows system admins to enable debug mode
 * @returns [boolean, (enabled: boolean) => void] tuple containing debug state and setter
 */
export type DebugLoggerFn = (...args: unknown[]) => void;

/**
 * Returns a console.debug function with a styled context prefix.
 * Does not check debug flags; always logs when called.
 */
export const debugLogger = (context?: string): DebugLoggerFn => {
  const prefix = context ? `[${context}]` : undefined;
  const style =
    'background:#2563EB;color:#FFFFFF;padding:0 4px;border-radius:3px;font-weight:600;';

  const logger: DebugLoggerFn = (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    const consoleMethod = console.debug as (...args: unknown[]) => void;
    if (prefix) {
      consoleMethod(`%c${prefix}`, style, ...args);
    } else {
      consoleMethod(...args);
    }
  };

  return logger;
};

export const useDebugLogger = (context?: string): DebugLoggerFn => {
  const isDebugEnabled = useFlagsStore((s) => s.debug);
  const baseLogger = useMemo(() => debugLogger(context), [context]);
  // Use a ref to store the current debug state so the returned function
  // can be stable while still checking the current debug state when called
  const isDebugEnabledRef = useRef(isDebugEnabled);

  useEffect(() => {
    isDebugEnabledRef.current = isDebugEnabled;
  }, [isDebugEnabled]);

  // Return a stable function that checks debug state at call time
  return useMemo<DebugLoggerFn>(() => {
    return (...args: unknown[]) => {
      if (isDebugEnabledRef.current) {
        baseLogger(...args);
      }
    };
  }, [baseLogger]);
};
